import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Loader } from "@mantine/core";
import { https } from "../../../../../http";
import { showError, showSuccess } from "../../../../../utils";
import SettingsSelect from "../SettingsSelect";

/**
 * ProgramsSection — lists the organization's Programs (each Program is a
 * Facility, 1:1) and lets an admin edit each program's Provider Information
 * (billing identity, reusing the live BillingProvider record), program type,
 * and address. Programs can inherit the org-level provider/address defaults.
 *
 * Props:
 *   orgAddress   { line1, city, state, postal_code } — org default address
 *   orgProvider  { providerName, ein, ssn, npi, taxonomy, billingId }
 */

const Chevron = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const Plus = () => (
  <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

// Derive identity from a BillingProvider record into the billing-style
// type+value pairs (Federal Tax ID = EIN/SSN, Billing Identifier = NPI/ID).
const deriveIdentity = (p = {}) => {
  const ftx = p.federalTaxID || {};
  const bid = p.billingIdentifier || {};
  // Prefer the legacy {type,number} objects; fall back to the flat fields.
  const ftxType = ftx.type || (p.ein ? "EIN" : p.ssn ? "SSN" : "");
  const ftxNumber = String(ftx.number || p.ein || p.ssn || "");
  const bidType = bid.type || (p.npi ? "NPI" : p.billingId ? "ID" : "");
  const bidNumber = String(
    (typeof bid === "object" ? bid.number : bid) || p.npi || p.billingId || ""
  );
  return {
    name: p.name || "",
    taxonomy: p.taxonomy || p.Taxonomy || "",
    ftxType,
    ftxNumber,
    bidType,
    bidNumber,
  };
};

// Map the org-level provider defaults into the same type+value shape.
const orgToIdentity = (o = {}) => ({
  name: o.providerName || "",
  taxonomy: o.taxonomy || "",
  ftxType: o.ein ? "EIN" : o.ssn ? "SSN" : "",
  ftxNumber: o.ein || o.ssn || "",
  bidType: o.npi ? "NPI" : o.billingId ? "ID" : "",
  bidNumber: o.npi || o.billingId || "",
});

const addressLine = (f = {}) => {
  const parts = [f.address, f.city, f.state, f.zipcode].filter(Boolean);
  return parts.length ? parts.join(", ") : "No address on file";
};

function ProgramRow({ facility, orgAddress, orgProvider, userIds, industryOptions, posOptions, onChanged }) {
  const fid = facility.id || facility._id;
  const facTypeId =
    facility.type?.id ||
    facility.type?._id ||
    (typeof facility.type === "string" ? facility.type : "");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [provider, setProvider] = useState(null);
  const [form, setForm] = useState(null);
  const [useOrgProvider, setUseOrgProvider] = useState(false);
  const [useOrgAddress, setUseOrgAddress] = useState(false);
  // Program type = the facility's care type (FType). Place of service = a
  // billing service-code, prefilled from the program's billing provider.
  const [programTypeVal, setProgramTypeVal] = useState(facTypeId);
  const [posVal, setPosVal] = useState(facility.placeOfService || "");
  const [addr, setAddr] = useState({
    street: facility.address || "",
    city: facility.city || "",
    state: facility.state || "",
    zip: facility.zipcode || "",
  });
  const [savingRow, setSavingRow] = useState(false);

  const subtype =
    typeof facility.type === "object" ? facility.type?.name : facility.typeName;

  const loadProvider = async () => {
    if (loaded) return;
    try {
      setLoading(true);
      const res = await https().get(
        `billingprovider/billing-provider-by-facility-id/${fid}?page=1`
      );
      const list =
        res?.data?.billingprovider || res?.data?.results || res?.data || [];
      const primary = Array.isArray(list) ? list[0] : list;
      setProvider(primary || null);
      setForm(deriveIdentity(primary || {}));
      setUseOrgProvider(Boolean(primary?.useOrgProvider));
      setUseOrgAddress(Boolean(primary?.useOrgAddress));
      // Automate Place of service from the program's billing provider code.
      if (!facility.placeOfService && primary?.serviceCode?.code) {
        setPosVal(primary.serviceCode.code);
      }
    } catch (e) {
      setForm(deriveIdentity({}));
    } finally {
      setLoading(false);
      setLoaded(true);
    }
  };

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) loadProvider();
  };

  const setField = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));
  const setAddrField = (k) => (e) =>
    setAddr((a) => ({ ...a, [k]: e?.target ? e.target.value : e }));

  // Values shown in the provider grid — org defaults when inheriting.
  const shown = useOrgProvider ? orgToIdentity(orgProvider) : form;

  const handleSave = async () => {
    setSavingRow(true);
    try {
      const base = {
        useOrgProvider,
        useOrgAddress,
        facilityId: fid,
        admin: userIds.admin,
        createdBy: userIds.createdBy,
      };
      // identity: when inheriting, persist the org defaults onto the record so
      // claims keep a concrete value; otherwise persist the program's own.
      // Stored as billing-style {type, number} objects (federalTaxID / billingIdentifier).
      const src = useOrgProvider ? orgToIdentity(orgProvider) : form;
      const identity = {
        name: src.name,
        taxonomy: src.taxonomy,
        federalTaxID: { type: src.ftxType || "", number: src.ftxNumber || "" },
        billingIdentifier: { type: src.bidType || "", number: src.bidNumber || "" },
      };
      // address: org default when inheriting, else the program's own.
      const address = useOrgAddress
        ? {
            address: orgAddress?.line1 || "",
            city: orgAddress?.city || "",
            state: orgAddress?.state || "",
            zipcode: orgAddress?.postal_code || "",
          }
        : { address: addr.street, city: addr.city, state: addr.state, zipcode: addr.zip };

      const payload = { ...base, ...identity, ...address };
      const pid = provider?.id || provider?._id;
      if (pid) {
        await https().put(`/billingprovider/${pid}`, { ...payload, id: pid });
      } else {
        const res = await https().post("billingprovider", payload);
        setProvider(res?.data?.billingProvider || null);
      }

      // program type (FType) + place of service — side-effect-free endpoint
      const facSet = {};
      if (programTypeVal && programTypeVal !== facTypeId) facSet.type = programTypeVal;
      if (posVal !== (facility.placeOfService || "")) facSet.placeOfService = posVal;
      if (Object.keys(facSet).length) {
        await https().put(`/facility/program-type/${fid}`, facSet);
        if (facSet.placeOfService !== undefined) facility.placeOfService = posVal;
      }

      showSuccess("Program saved");
      onChanged?.();
    } catch (e) {
      showError(e?.response?.data?.message || "Could not save program");
    } finally {
      setSavingRow(false);
    }
  };

  return (
    <div className={`prog${open ? " open" : ""}`}>
      <button type="button" className="prog-head" onClick={toggle}>
        <div className="prog-id">
          <span className="prog-name">{facility.name || "Untitled program"}</span>
          <span className="prog-sub">{addressLine(facility)}</span>
        </div>
        {subtype ? <span className="prog-badge facility">{subtype}</span> : null}
        <span className="prog-chev"><Chevron /></span>
      </button>

      {open && (
        <div className="prog-body">
          {loading || !form ? (
            <div className="prog-loading"><Loader size="sm" color="#FFBA45" /></div>
          ) : (
            <>
              {/* Program type (care type) + Place of service (billing code) */}
              <div className="form-grid two-col">
                <div>
                  <label className="label">Program type</label>
                  <SettingsSelect
                    value={programTypeVal}
                    onChange={setProgramTypeVal}
                    placeholder="Select program type"
                    options={industryOptions || []}
                  />
                </div>
                <div>
                  <label className="label">Place of service</label>
                  <div style={{ display: "flex", gap: 8, alignItems: "stretch" }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <SettingsSelect
                        value={posVal}
                        onChange={setPosVal}
                        placeholder="Select place of service"
                        options={posOptions || []}
                      />
                    </div>
                    <input
                      className="input"
                      style={{ width: 96, flex: "none" }}
                      value={posVal}
                      readOnly
                      placeholder="Code"
                      title="Place of service code"
                    />
                  </div>
                </div>
              </div>

              {/* Provider Information */}
              <div className="prog-sub-title">Provider information</div>
              <label className="prog-toggle">
                <input
                  type="checkbox"
                  checked={useOrgProvider}
                  onChange={(e) => setUseOrgProvider(e.target.checked)}
                />
                <span className="prog-toggle-box" />
                <span className="prog-toggle-txt">Use organization provider info</span>
              </label>
              <div className="form-grid two-col">
                <div>
                  <label className="label">Provider name</label>
                  <input className="input" value={shown.name} onChange={setField("name")} disabled={useOrgProvider} placeholder="Provider / billing entity name" />
                </div>
                <div>
                  <label className="label">Taxonomy / Specialty</label>
                  <input className="input" value={shown.taxonomy} onChange={setField("taxonomy")} disabled={useOrgProvider} placeholder="e.g. 251E00000X" />
                </div>
                <div>
                  <label className="label">Federal Tax ID</label>
                  <div className="id-inline">
                    <div className="id-inline-type">
                      <SettingsSelect
                        value={shown.ftxType}
                        onChange={setField("ftxType")}
                        disabled={useOrgProvider}
                        placeholder="Type"
                        options={[
                          { value: "EIN", label: "EIN" },
                          { value: "SSN", label: "SSN" },
                        ]}
                      />
                    </div>
                    <input
                      className="input"
                      value={shown.ftxNumber}
                      onChange={setField("ftxNumber")}
                      disabled={useOrgProvider}
                      placeholder={shown.ftxType === "SSN" ? "000-00-0000" : "00-0000000"}
                    />
                  </div>
                </div>
                <div>
                  <label className="label">Billing Identifier</label>
                  <div className="id-inline">
                    <div className="id-inline-type">
                      <SettingsSelect
                        value={shown.bidType}
                        onChange={setField("bidType")}
                        disabled={useOrgProvider}
                        placeholder="Type"
                        options={[
                          { value: "NPI", label: "NPI" },
                          { value: "ID", label: "ID" },
                        ]}
                      />
                    </div>
                    <input
                      className="input"
                      value={shown.bidNumber}
                      onChange={setField("bidNumber")}
                      disabled={useOrgProvider}
                      placeholder={shown.bidType === "NPI" ? "10-digit NPI" : "Billing / payer ID"}
                    />
                  </div>
                </div>
              </div>

              {/* Address */}
              <div className="prog-sub-title">Address</div>
              <label className="prog-toggle">
                <input
                  type="checkbox"
                  checked={useOrgAddress}
                  onChange={(e) => setUseOrgAddress(e.target.checked)}
                />
                <span className="prog-toggle-box" />
                <span className="prog-toggle-txt">Use organization address</span>
              </label>

              {useOrgAddress ? (
                <div className="prog-org-addr">
                  {orgAddress?.line1
                    ? [orgAddress.line1, orgAddress.city, orgAddress.state, orgAddress.postal_code]
                        .filter(Boolean)
                        .join(", ")
                    : "No organization address set yet. Add it in Address above."}
                </div>
              ) : (
                <div className="form-grid two-col">
                  <div style={{ gridColumn: "1 / -1" }}>
                    <label className="label">Street</label>
                    <input className="input" value={addr.street} onChange={setAddrField("street")} placeholder="Street address" />
                  </div>
                  <div>
                    <label className="label">City</label>
                    <input className="input" value={addr.city} onChange={setAddrField("city")} placeholder="City" />
                  </div>
                  <div>
                    <label className="label">State</label>
                    <input className="input" value={addr.state} onChange={setAddrField("state")} placeholder="State" />
                  </div>
                  <div>
                    <label className="label">Zip</label>
                    <input className="input" value={addr.zip} onChange={setAddrField("zip")} placeholder="00000" />
                  </div>
                </div>
              )}

              <div className="prog-foot">
                <span className="prog-note">
                  Provider identity is shared with the Billing tab.
                </span>
                <button type="button" className="btn primary" onClick={handleSave} disabled={savingRow}>
                  {savingRow ? <Loader size="xs" color="#171717" /> : "Update"}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function AddProgramForm({ onCreate, onCancel, creating }) {
  const [name, setName] = useState("");
  const [programType, setProgramType] = useState("facility");
  const [street, setStreet] = useState("");
  const [city, setCity] = useState("");
  const [stateVal, setStateVal] = useState("");
  const [zip, setZip] = useState("");

  return (
    <div className="prog prog-add open">
      <div className="prog-body" style={{ borderTop: "none", paddingTop: 16 }}>
        <div className="prog-sub-title" style={{ margin: "0 0 12px", padding: 0, borderTop: "none" }}>New program</div>
        <div className="form-grid two-col">
          <div>
            <label className="label">Program name</label>
            <input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Sunrise Group Home" />
          </div>
          <div>
            <label className="label">Program type</label>
            <SettingsSelect
              value={programType}
              onChange={setProgramType}
              options={[
                { value: "facility", label: "Facility" },
                { value: "agency", label: "Agency" },
              ]}
            />
          </div>
          <div style={{ gridColumn: "1 / -1" }}>
            <label className="label">Street</label>
            <input className="input" value={street} onChange={(e) => setStreet(e.target.value)} placeholder="Street address" />
          </div>
          <div>
            <label className="label">City</label>
            <input className="input" value={city} onChange={(e) => setCity(e.target.value)} placeholder="City" />
          </div>
          <div>
            <label className="label">State</label>
            <input className="input" value={stateVal} onChange={(e) => setStateVal(e.target.value)} placeholder="State" />
          </div>
          <div>
            <label className="label">Zip</label>
            <input className="input" value={zip} onChange={(e) => setZip(e.target.value)} placeholder="00000" />
          </div>
        </div>
        <div className="prog-foot">
          <button type="button" className="btn" onClick={onCancel} disabled={creating}>Cancel</button>
          <button
            type="button"
            className="btn primary"
            disabled={creating || !name.trim()}
            onClick={() =>
              onCreate({
                name: name.trim(),
                programType,
                address: street,
                location: street,
                state: stateVal,
                zipcode: zip,
                city,
              })
            }
          >
            {creating ? <Loader size="xs" color="#171717" /> : "Create program"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ProgramsSection({ orgAddress, orgProvider }) {
  const reduxProfile = useSelector((state) => state?.profile?.profile);
  const userIds = {
    createdBy: reduxProfile?.user?.id,
    admin: reduxProfile?.user?.admin ?? reduxProfile?.user?.id,
  };

  const [facilities, setFacilities] = useState(null);
  const [error, setError] = useState("");
  const [adding, setAdding] = useState(false);
  const [creating, setCreating] = useState(false);
  // Program-type options = the org's Facility Types (FType list).
  const [industryOptions, setIndustryOptions] = useState([]);
  // Place-of-service options = billing service codes.
  const [posOptions, setPosOptions] = useState([]);

  const fetchFacilities = async () => {
    try {
      const res = await https().get("/facility/all");
      const list =
        res?.data?.facilities || res?.data?.results || res?.data || [];
      setFacilities(Array.isArray(list) ? list : []);
    } catch (e) {
      setError("Could not load programs.");
    }
  };

  useEffect(() => {
    fetchFacilities();
    (async () => {
      try {
        const res = await https().get("/ftype/all");
        const types = res?.data?.ftypes || res?.data?.results || [];
        if (Array.isArray(types)) {
          setIndustryOptions(
            types
              .filter((t) => t?.name)
              .map((t) => ({ value: t.id || t._id, label: t.name }))
          );
        }
      } catch (e) {
        /* leave program-type options empty */
      }
      try {
        const res = await https().get("servicecode/all");
        const codes = res?.data?.servicescode || res?.data?.results || [];
        if (Array.isArray(codes)) {
          setPosOptions(
            codes
              .filter((c) => c?.code)
              .map((c) => ({ value: c.code, label: c.name || c.code }))
              .sort((a, b) => a.label.localeCompare(b.label))
          );
        }
      } catch (e) {
        /* leave place-of-service options empty */
      }
    })();
  }, []);

  const handleCreate = async (data) => {
    setCreating(true);
    try {
      await https().post("/facility", data);
      showSuccess("Program created");
      setAdding(false);
      setFacilities(null);
      await fetchFacilities();
    } catch (e) {
      showError(e?.response?.data?.message || "Could not create program");
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="card">
      <div className="card-section">
        <div className="prog-head-row">
          <div>
            <div className="card-title">Programs</div>
            <div className="card-subtitle">
              Each facility or agency you operate. Billing and provider identity live here.
            </div>
          </div>
          {!adding && (
            <button type="button" className="btn sm" onClick={() => setAdding(true)}>
              <Plus /> Add program
            </button>
          )}
        </div>

        {adding && (
          <div style={{ marginTop: 14 }}>
            <AddProgramForm onCreate={handleCreate} onCancel={() => setAdding(false)} creating={creating} />
          </div>
        )}

        {facilities === null && !error ? (
          <div className="prog-loading" style={{ marginTop: 16 }}>
            <Loader size="sm" color="#FFBA45" />
          </div>
        ) : error ? (
          <div className="field-hint" style={{ marginTop: 16 }}>{error}</div>
        ) : facilities.length === 0 ? (
          <div className="field-hint" style={{ marginTop: 16 }}>
            No programs yet. Add a facility to create your first program.
          </div>
        ) : (
          <div className="prog-list">
            {facilities.map((f) => (
              <ProgramRow
                key={f.id || f._id}
                facility={f}
                orgAddress={orgAddress}
                orgProvider={orgProvider}
                userIds={userIds}
                industryOptions={industryOptions}
                posOptions={posOptions}
                onChanged={fetchFacilities}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
